import { Link } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { Badge, PageTitle, card, linkCls } from "@/components/admin/ui";
import { MaterialIcon } from "@/components/MaterialIcon";
import { money } from "@/lib/format";

const tone = (s) => ({ paid: "green", pending: "yellow", refunded: "blue", failed: "red", expired: "gray" }[s] || "gray");

const Stat = ({ label, value, icon, testId }) => (
  <div className={`${card} p-4`} data-testid={testId}>
    <div className="flex items-center gap-2 text-[#6B7280]">
      <MaterialIcon name={icon} size={18} weight={400} />
      <p className="text-[12px]">{label}</p>
    </div>
    <p className="mt-1.5 text-[24px] md:text-[28px] font-semibold leading-tight">{value}</p>
  </div>
);

export default function AdminHome() {
  const { user } = useAuth();
  const { data } = useApi("/admin/sales");
  const { data: videos } = useApi("/admin/videos");
  if (!data) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  const totals = Object.entries(data.totals);
  const currency = totals[0]?.[0] || "usd";
  const recent = data.sales.slice(0, 6);
  const liveVideos = (videos || []).filter((v) => v.visibility === "public").length;

  return (
    <div data-testid="admin-home-page">
      <PageTitle title={`Welcome back${user?.organizer_for ? `, ${user.organizer_for}` : ""}`}>
        {data.mode === "test" && <Badge tone="yellow">Stripe test mode</Badge>}
      </PageTitle>

      <div className={`mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 ${data.is_admin ? "xl:grid-cols-3" : "xl:grid-cols-4"}`}>
        <Stat label="Passes sold" value={data.paid_count} icon="confirmation_number" testId="home-stat-passes" />
        <Stat
          label="Revenue"
          value={totals.length ? totals.map(([c, a]) => money(a, c)).join(" · ") : money(0, "usd")}
          icon="payments"
          testId="home-stat-revenue"
        />
        {!data.is_admin && (
          <Stat
            label={`Your earnings (after ${data.platform_fee_pct}%)`}
            value={money(data.earnings, currency)}
            icon="account_balance"
            testId="home-stat-earnings"
          />
        )}
        <Stat label="Videos live" value={liveVideos} icon="movie" testId="home-stat-videos" />
      </div>

      <div className={`${card} overflow-hidden`} data-testid="home-recent-orders">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2.5">
          <p className="text-[12px] font-medium text-[#6B7280]">Recent orders</p>
          <Link to="/admin/sales" className={`text-[12px] ${linkCls}`} data-testid="home-view-all-sales">
            View all sales
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-[13px]">
            <thead className="bg-white text-left text-[12px] text-[#6B7280]">
              <tr className="border-b border-[#E5E7EB]">
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Festival</th>
                <th className="px-4 py-2.5 font-medium">Pass</th>
                <th className="px-4 py-2.5 font-medium">Buyer</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {recent.map((s) => (
                <tr key={s.session_id} data-testid="home-order-row">
                  <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2.5 font-medium">{s.festival_name}</td>
                  <td className="px-4 py-2.5 text-[#374151]">{s.tier_name}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]">
                    {s.buyer_email ? (
                      <Link to={`/admin/customers/${encodeURIComponent(s.buyer_email)}`} className={linkCls}>{s.buyer_email}</Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium">{money(s.amount_total, s.currency)}</td>
                  <td className="px-4 py-2.5"><Badge tone={tone(s.payment_status)}>{s.payment_status}</Badge></td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]" data-testid="home-orders-empty">
                    No orders yet. They appear here as soon as someone buys a pass.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
